import classnames from 'classnames';
import Icon from 'app/components/partials/icon';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { extractQueryParams, parseVideoUrl } from 'app/utilities/video';
import React, { forwardRef, useEffect, useRef, useState } from 'react';

const HeroVideo = forwardRef(({
    videoUrl,
    title,
    type = '',
    styles = '',
    autoplay = false,
    muted = true,
    loop = true,
    controls = false,
    buttonLabel,
    buttonLink
}, ref) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [error, setError] = useState(null);
    const [videoInfo, setVideoInfo] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [urlParams, setUrlParams] = useState({});
    const iframeRef = useRef(null);
    const playerRef = useRef(null);

    // Disable autoplay on mobile devices
    const isMobile = () => {
        if (typeof window === 'undefined' || typeof navigator === 'undefined') {
            return false;
        }

        const MOBILE_BREAKPOINT = 768;
        const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

        return mobileRegex.test(navigator.userAgent) || window.innerWidth <= MOBILE_BREAKPOINT;
    };

    // Get final values considering URL parameters and props
    const finalAutoplay = Object.prototype.hasOwnProperty.call(urlParams, 'autoplay') ? (urlParams.autoplay === true || urlParams.autoplay === 1) : autoplay;
    const finalMuted = Object.prototype.hasOwnProperty.call(urlParams, 'mute') ?
        (urlParams.mute === true || urlParams.mute === 1 || urlParams.mute === true || urlParams.mute === 1) : muted;
    const finalLoop = Object.prototype.hasOwnProperty.call(urlParams, 'loop') ? (urlParams.loop === true || urlParams.loop === 1) : loop;
    const finalControls = Object.prototype.hasOwnProperty.call(urlParams, 'controls') ? (urlParams.controls === true || urlParams.controls === 1) : controls;
    const shouldAutoplay = finalAutoplay && !isMobile();

    // Handle play/pause functionality
    const handlePlayPause = () => {
        if (isPlaying) {
            // Pause video
            if (videoInfo && videoInfo.type === 'youtube' && playerRef.current) {
                playerRef.current.pauseVideo();
            } else if (videoInfo && videoInfo.type === 'vimeo' && playerRef.current) {
                playerRef.current.pause();
            }
            setIsPlaying(false);
        } else {
            // Play video
            if (videoInfo && videoInfo.type === 'youtube' && playerRef.current) {
                playerRef.current.playVideo();
            } else if (videoInfo && videoInfo.type === 'vimeo' && playerRef.current) {
                playerRef.current.play();
            }
            setIsPlaying(true);
        }
    };

    // Initialize YouTube player
    const initializeYouTubePlayer = () => {
        if (window.YT && videoInfo && videoInfo.type === 'youtube') {
            const player = new window.YT.Player(iframeRef.current, {
                videoId: videoInfo.id,
                playerVars: {
                    autoplay: shouldAutoplay ? 1 : 0,
                    mute: finalMuted ? 1 : 0,
                    loop: finalLoop ? 1 : 0,
                    playlist: finalLoop ? videoInfo.id : '',
                    controls: finalControls ? 1 : 0,
                    rel: 0,
                    showinfo: 0,
                    modestbranding: 1,
                    playsinline: 1
                },
                events: {
                    onReady: (event) => {
                        playerRef.current = event.target;
                        setIsLoaded(true);
                        if (shouldAutoplay) {
                            setIsPlaying(true);
                        }
                    },
                    onStateChange: (event) => {
                        if (event.data === window.YT.PlayerState.PLAYING) {
                            setIsPlaying(true);
                        } else if (event.data === window.YT.PlayerState.PAUSED ||
                                   event.data === window.YT.PlayerState.ENDED) {
                            setIsPlaying(false);
                        }
                    },
                    onError: (event) => {
                        console.error('YouTube player error:', event.data);
                        // Set loaded to true even on error to hide loading spinner
                        setIsLoaded(true);
                    }
                }
            });

            return player;
        }

        return null;
    };

    // Initialize Vimeo player
    const initializeVimeoPlayer = () => {
        if (window.Vimeo && videoInfo && videoInfo.type === 'vimeo') {
            // Wait for iframe to load before initializing player
            const iframe = iframeRef.current;

            const onIframeLoad = () => {
                // Initialize the Vimeo player after iframe loads
                const player = new window.Vimeo.Player(iframe, {
                    id: videoInfo.id,
                    autoplay: false, // Start with autoplay disabled
                    muted: finalMuted,
                    loop: finalLoop,
                    controls: false, // Hide controls on both desktop and mobile
                    title: false,
                    byline: false,
                    portrait: false,
                    playsinline: true
                });

                playerRef.current = player;
                setIsLoaded(true);

                player.on('play', () => {
                    setIsPlaying(true);
                });

                player.on('pause', () => {
                    setIsPlaying(false);
                });

                player.on('ended', () => {
                    setIsPlaying(false);
                });

                if (shouldAutoplay) {
                    setIsPlaying(true);
                    player.play();
                }

                // Remove the load event listener
                iframe.removeEventListener('load', onIframeLoad);
            };

            // Add load event listener to iframe
            iframe.addEventListener('load', onIframeLoad);
        }
    };

    useEffect(() => {
        if (!videoUrl) return;

        const videoInfo = parseVideoUrl(videoUrl);
        
        if (!videoInfo) {
            setError('Unsupported video URL');
            return;
        }

        if (videoInfo.provider !== 'youtube' && videoInfo.provider !== 'vimeo') {
            setError(`Unsupported video provider: ${videoInfo.provider}`);
            return;
        }

        setVideoInfo({
            type: videoInfo.provider,
            id: videoInfo.id
        });

        // Extract query parameters from the video URL
        const params = extractQueryParams(videoUrl);
        setUrlParams(params);
    }, [videoUrl]);

    useEffect(() => {
        if (!videoInfo || !videoInfo.type) return;

        // Load YouTube API if needed
        if (videoInfo.type === 'youtube' && !window.YT) {
            const tag = document.createElement('script');
            tag.src = 'https://www.youtube.com/iframe_api';
            const firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

            window.onYouTubeIframeAPIReady = () => {
                initializeYouTubePlayer();
            };
        } else if (videoInfo.type === 'youtube' && window.YT) {
            // YouTube API already loaded, initialize player
            initializeYouTubePlayer();
        } else if (videoInfo.type === 'vimeo') {
            // Load Vimeo Player API if needed
            if (window.Vimeo) {
                initializeVimeoPlayer();
            } else {
                const script = document.createElement('script');
                script.src = 'https://player.vimeo.com/api/player.js';
                script.onload = () => {
                    initializeVimeoPlayer();
                };
                document.head.appendChild(script);
            }
        }
    }, [videoInfo, shouldAutoplay, finalMuted, finalLoop, finalControls]);

    if (!videoUrl) {
        return null;
    }

    if (error) {
        return (
            <header ref={ref} className="hero-video-wrapper">
                <div className={`hero-video ${type} ${styles} error`}>
                    <div className="hero-video-error">
                        <p>{error}</p>
                    </div>
                </div>
            </header>
        );
    }

    const renderYouTubeEmbed = () => {
        // Set initial src for YouTube iframe so the API can initialize properly
        const embedUrl = `https://www.youtube.com/embed/${videoInfo.id}?enablejsapi=1&autoplay=${shouldAutoplay ? 1 : 0}&mute=${finalMuted ? 1 : 0}&loop=${finalLoop ? 1 : 0}${finalLoop ? `&playlist=${videoInfo.id}` : ''}&controls=${finalControls ? 1 : 0}&rel=0&showinfo=0&modestbranding=1&playsinline=1`;

        return (
            <div className="video-iframe-container">
                <iframe
                    ref={iframeRef}
                    src={embedUrl}
                    title={title || 'YouTube video'}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className={classnames('hero-video-element', {
                        'is-loaded': isLoaded
                    })}
                />
            </div>
        );
    };

    const renderVimeoEmbed = () => {
        // Set initial src for Vimeo iframe
        const embedUrl = `https://player.vimeo.com/video/${videoInfo.id}?autoplay=0&muted=${finalMuted ? 1 : 0}&loop=${finalLoop ? 1 : 0}&controls=0&title=0&byline=0&portrait=0&playsinline=1`;

        return (
            <div className="video-iframe-container">
                <iframe
                    ref={iframeRef}
                    src={embedUrl}
                    title={title || 'Vimeo video'}
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    className={classnames('hero-video-element', {
                        'is-loaded': isLoaded
                    })}
                />
            </div>
        );
    };

    const renderPlayButton = () => {
        // Only show play button on mobile when video is loaded, not autoplaying, and not playing
        if (isMobile() && isLoaded && !shouldAutoplay && !isPlaying) {
            return (
                <button
                    className="video-play-overlay"
                    onClick={handlePlayPause}
                    type="button"
                    aria-label="Play video"
                >
                    <div className="play-button">
                        <Icon name="play" width="60" height="60" />
                    </div>
                </button>
            );
        }

        return null;
    };

    return (
        <header ref={ref} className="hero-video-wrapper">
            <div className={`hero-video ${type} ${styles}`}>
                <div className="video-container">
                    {videoInfo && videoInfo.type === 'youtube' && renderYouTubeEmbed()}
                    {videoInfo && videoInfo.type === 'vimeo' && renderVimeoEmbed()}
                    {renderPlayButton()}

                    {!isLoaded && !error && (
                        <div className="video-loading">
                            <div className="loading-spinner"></div>
                        </div>
                    )}
                </div>
            </div>
            <Link className="hero-video-content constrain-width large" to={buttonLink}>
                {title && <h1 className="heading-1">{title}</h1>}
                {buttonLabel && <p className="button-label">{buttonLabel}</p>}
                <Icon name="chevron" width="80" height="80" />
            </Link>
        </header>
    );
});

HeroVideo.propTypes = {
    videoUrl: PropTypes.string.isRequired,
    title: PropTypes.string,
    type: PropTypes.string,
    styles: PropTypes.string,
    autoplay: PropTypes.bool,
    muted: PropTypes.bool,
    loop: PropTypes.bool,
    controls: PropTypes.bool,
    buttonLabel: PropTypes.string,
    buttonLink: PropTypes.string
};

HeroVideo.displayName = 'HeroVideo';

export default HeroVideo;
